// src/widgets/DataTable.jsx
import {
  ChevronsLeft, ChevronsRight,
  ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown,
  ArrowUpDown, CheckSquare, Square
} from "lucide-react"

export default function DataTable({
  className = "",
  columns = [],
  rows = [],
  loading = false,
  page = 1,
  perPage = 10,
  total = 0,
  onPageChange,
  onPerPageChange,
  sortBy,
  sortDir,
  onSortChange,
  emptyText = "– ไม่มีข้อมูล –",
  sizes = [10, 20, 50, 100],
  stickyHeader = true,
  selectable = false,
  rowKey = (row, idx) => row.__key ?? String(idx),
  selectedSet,
  onToggleRow,
  onToggleAll,
  onRowClick,
}) {
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, perPage)))
  const canPrev = page > 1
  const canNext = page < totalPages

  const handleSort = (col) => {
    if (!col.sortable) return
    const key = col.sortKey || col.key
    const nextDir = sortBy === key && sortDir === "asc" ? "desc" : "asc"
    onSortChange?.(key, nextDir)
  }

  const normalCols = columns.filter(c => !c.pinEnd)
  const pinnedEndCols = columns.filter(c => c.pinEnd)
  const orderedCols = [...normalCols, ...pinnedEndCols]

  const HeaderCell = ({ col }) => {
    const isSortable = !!col.sortable
    const key = col.sortKey || col.key
    const isActive = isSortable && sortBy === key
    const align =
      col.align === "right" ? "text-right" :
      col.align === "center" ? "text-center" : "text-left"
    const style = col.width ? { width: typeof col.width === "number" ? `${col.width}px` : col.width } : undefined
    const stickyRight = col.pinEnd ? "sticky right-0 z-30" : ""
    const topFix = stickyHeader ? "top-0" : ""

    let Icon = null
    if (isSortable) {
      if (isActive) {
        Icon = sortDir === "asc"
          ? <ChevronUp size={14} className="shrink-0 text-gray-900" />
          : <ChevronDown size={14} className="shrink-0 text-gray-900" />
      } else {
        Icon = <ArrowUpDown size={14} className="shrink-0 text-gray-400" />
      }
    }

    return (
      <th
        className={`py-2 px-3 ${align} ${col.headerClassName || ""} ${stickyRight} ${topFix} ${col.pinEnd ? "bg-gray-100 border-l border-gray-200" : ""}`}
        scope="col"
        style={style}
      >
        {isSortable ? (
          <button
            type="button"
            onClick={() => handleSort(col)}
            className={`inline-flex items-center gap-1 select-none cursor-pointer ${isActive ? "font-semibold text-gray-900" : "text-gray-700 hover:text-gray-900"}`}
            aria-label={`sort by ${col.title}`}
          >
            <span>{col.title}</span>
            {Icon}
          </button>
        ) : (
          <span className="text-gray-700">{col.title}</span>
        )}
      </th>
    )
  }

  const keysOnPage = rows.map((r, i) => rowKey(r, i))
  const allSelected = selectable && selectedSet && keysOnPage.length > 0 && keysOnPage.every(k => selectedSet.has(k))

  return (
    <div className={`w-full h-full min-h-0 flex flex-col bg-white rounded-xl shadow-md overflow-hidden ${className}`}>
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="min-w-[820px] w-full text-sm">
          <thead className={`${stickyHeader ? "sticky top-0" : ""} bg-gray-100 text-gray-600`}>
            <tr>
              {selectable && (
                <th className="py-2 px-3 w-10 text-left">
                  <button
                    type="button"
                    className="text-primary hover:text-primary/80 cursor-pointer"
                    disabled={rows.length === 0 || !onToggleAll}
                    onClick={() => onToggleAll?.(keysOnPage, rows)}
                    aria-label="toggle select all"
                  >
                    {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                  </button>
                </th>
              )}
              {orderedCols.map(col => (
                <HeaderCell key={col.key} col={col} />
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td className="py-6 text-center text-gray-500" colSpan={orderedCols.length + (selectable ? 1 : 0)}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="py-6 text-center text-gray-500" colSpan={orderedCols.length + (selectable ? 1 : 0)}>
                  {emptyText}
                </td>
              </tr>
            ) : (
              rows.map((row, ridx) => {
                const k = rowKey(row, ridx)
                const checked = selectable && selectedSet?.has(k)
                return (
                  <tr
                    key={k}
                    className={`odd:bg-white even:bg-gray-50 ${onRowClick || selectable ? "cursor-pointer" : ""} whitespace-nowrap`}
                    onClick={() => {
                      if (onRowClick) onRowClick(row, ridx)
                      else if (selectable && onToggleRow) onToggleRow(k, row)
                    }}
                  >
                    {selectable && (
                      <td className="py-2 px-3 text-center">
                        <button
                          type="button"
                          className="text-primary hover:text-primary/80"
                          onClick={(e) => { e.stopPropagation(); onToggleRow?.(k, row) }}
                          aria-label="toggle row"
                        >
                          {checked ? <CheckSquare size={16} /> : <Square size={16} />}
                        </button>
                      </td>
                    )}
                    {orderedCols.map(col => {
                      const align =
                        col.align === "right" ? "text-right" :
                        col.align === "center" ? "text-center" : "text-left"
                      const style = col.width ? { width: typeof col.width === "number" ? `${col.width}px` : col.width } : undefined
                      const stickyRight = col.pinEnd ? "sticky right-0 z-20 bg-white border-l border-gray-200" : ""
                      return (
                        <td
                          key={col.key}
                          className={`py-2 px-3 ${align} ${col.cellClassName || ""} ${stickyRight}`}
                          style={style}
                        >
                          {typeof col.render === "function" ? col.render(row, ridx) : row[col.key]}
                        </td>
                      )
                    })}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="shrink-0 bg-white border-t border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-3 p-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">แสดงต่อหน้า</span>
            <select
              value={perPage}
              onChange={e => {
                const val = parseInt(e.target.value) || 10
                onPerPageChange?.(val)
              }}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm"
            >
              {sizes.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="text-sm text-gray-600">
              {total.toLocaleString()} รายการ • หน้า {page}/{Math.max(1, Math.ceil(total / Math.max(1, perPage)))}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <button
              disabled={!canPrev}
              onClick={() => onPageChange?.(1)}
              className="p-2 rounded-md border disabled:opacity-40"
              aria-label="first page"
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              disabled={!canPrev}
              onClick={() => onPageChange?.(page - 1)}
              className="p-2 rounded-md border disabled:opacity-40"
              aria-label="previous page"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              disabled={!canNext}
              onClick={() => onPageChange?.(page + 1)}
              className="p-2 rounded-md border disabled:opacity-40"
              aria-label="next page"
            >
              <ChevronRight size={16} />
            </button>
            <button
              disabled={!canNext}
              onClick={() => onPageChange?.(totalPages)}
              className="p-2 rounded-md border disabled:opacity-40"
              aria-label="last page"
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
